import { useState } from "react";
import { Hand, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/common";

const ReasonDialog = ({ open, onOpenChange, title, description, label, confirmLabel, onConfirm, danger, prefix }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!reason.trim()) return setError("Indiquez la raison.");
    setBusy(true);
    setError("");
    const err = await onConfirm(reason);
    setBusy(false);
    if (err) setError(err);
    else setReason("");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${prefix}-dialog`} className="border-border bg-card">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <label className="text-sm font-semibold" htmlFor={`${prefix}-reason`}>{label}</label>
        <Textarea id={`${prefix}-reason`} data-testid={`${prefix}-reason-input`} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} className="text-base" />
        <FieldError error={error} testId={`${prefix}-error`} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${prefix}-cancel`}>Annuler</Button>
          <Button data-testid={`${prefix}-confirm`} onClick={confirm} disabled={busy} variant={danger ? "destructive" : "default"} className="font-bold">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StopButton = ({ onStop, disabled, className = "h-14 w-full border-2 px-2 sm:text-base" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" data-testid="stop-work-button" variant="outline" disabled={disabled} onClick={() => setOpen(true)}
        className={`${className} border-red-500 bg-red-950/40 text-sm font-bold text-red-100 hover:bg-red-900/60 hover:text-white`}>
        <Hand className="mr-2 h-5 w-5" /> Arrêter le travail
      </Button>
      <ReasonDialog open={open} onOpenChange={setOpen} prefix="stop" danger title="Arrêter le travail"
        description="Les conditions de sécurité ne sont pas réunies ? Arrêtez le travail et documentez la raison. Cette décision sera enregistrée."
        label="Raison de l'arrêt" confirmLabel="Confirmer l'arrêt"
        onConfirm={async (r) => { const err = await onStop(r); if (!err) setOpen(false); return err; }} />
    </>
  );
};

export const ReviseButton = ({ onRevise, className = "h-10 px-3" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" data-testid="revise-button" variant="outline" onClick={() => setOpen(true)} className={`${className} border-orange-500/60 text-orange-200 hover:bg-orange-950/50`}>
        <RefreshCw className="mr-2 h-4 w-4" /> Réévaluer
      </Button>
      <ReasonDialog open={open} onOpenChange={setOpen} prefix="revise" title="Réévaluer le Take 5"
        description="Une nouvelle révision sera créée. La version actuelle et sa décision sont conservées. Une nouvelle validation complète sera nécessaire."
        label="Raison de la réévaluation (changement de conditions…)" confirmLabel="Créer la révision"
        onConfirm={async (r) => { const err = await onRevise(r); if (!err) setOpen(false); return err; }} />
    </>
  );
};
